import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { StorageKeys } from '@core/constants';
import { RecaptchaDirective } from '@core/directives/recaptcha.directive';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { EnvironmentService } from '@core/services/environment.service';
import { FeatureFlagService } from '@core/services/feature-flag/feature-flag.service';
import { StorageService } from '@core/services/storage/storage.service';
import { RecaptchaModule } from 'ng-recaptcha-2';
import { ButtonDirective, ButtonLabel } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputOtpModule } from 'primeng/inputotp';
import { Ripple } from 'primeng/ripple';
import { interval, Subscription, take } from 'rxjs';

@Component({
  selector: 'au-otp',
  imports: [
    InputOtpModule,
    FormsModule,
    ButtonDirective,
    Ripple,
    RecaptchaDirective,
    RecaptchaModule,
    ButtonLabel,
  ],
  templateUrl: './otp.component.html',
})
export class OtpComponent implements OnInit, AfterViewInit {
  @ViewChild('turnstileRef') turnstileRef?: ElementRef<HTMLDivElement>;
  @ViewChild('otpInput', { read: ElementRef })
  otpInputRef?: ElementRef<HTMLElement>;
  otpCode: string = '';
  errorMessage: string = '';
  showError: any = false;
  data: any;
  isResendCodeDisabled: boolean = true;
  newRecaptchaResponse: boolean = false;
  countdown: number = 120;
  timerSubscription: Subscription | null = null;
  enableRecaptchaFlag: boolean = false;
  turnstileSiteKey: string = '';
  turnstileWidgetId: string | null = null;
  useTurnstile: boolean = false;
  verifiedSuccessfully: boolean = false;
  readonly NONE: string = 'NONE';
  readonly CLOUDFLARE_TURNSTILE: string = 'CLOUDFLARE_TURNSTILE';

  featureFlagService = inject(FeatureFlagService);
  storageService = inject(StorageService);
  environmentService = inject(EnvironmentService);

  constructor(
    private authService: AuthenticationService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.data = config.data;
    this.turnstileSiteKey = this.environmentService.turnstileSiteKey;
  }

  ngOnInit() {
    this.initFlags();
    this.requestOtpCode();
  }

  initFlags() {
    const captchaType =
      this.featureFlagService.featureFlagVariant('captcha-type')?.payload
        ?.value ?? this.NONE;

    this.useTurnstile = captchaType === this.CLOUDFLARE_TURNSTILE;

    this.enableRecaptchaFlag = captchaType !== this.NONE;
  }

  async ngAfterViewInit(): Promise<void> {
    this.otpInputRef?.nativeElement.addEventListener(
      'keydown',
      this.onOtpKeyDown,
      true
    );

    if (!this.enableRecaptchaFlag) {
      return;
    }

    if (this.useTurnstile) {
      await this.initializeTurnstile(this.turnstileRef!, (token: string) =>
        this.resolved(token)
      );
    }
  }

  startCountDown(countdown: number) {
    this.stopCountdown(); // Ensure any existing timer is stopped
    this.isResendCodeDisabled = true;
    this.otpCode = '';
    this.countdown = countdown;
    this.timerSubscription = interval(1000)
      .pipe(take(this.countdown))
      .subscribe({
        next: () => this.countdown--,
        complete: () => (this.isResendCodeDisabled = false),
      });
  }

  stopCountdown() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
  }

  resolved(captchaResponse: any) {
    if (captchaResponse) {
      this.newRecaptchaResponse = true;
      this.storageService.save(captchaResponse, StorageKeys.captchaResponse);

      if (this.useTurnstile) {
        this.verifiedSuccessfully = true;
      }
    }
  }

  requestOtpCode() {
    if (this.turnstileWidgetId) {
      this.verifiedSuccessfully = false;
      window.turnstile?.reset(this.turnstileWidgetId);
    }

    this.isResendCodeDisabled = true;
    this.newRecaptchaResponse = false;
    this.authService.requestOtpCode(this.data.email)?.subscribe({
      next: (res: any) => {
        if (res && res.success) {
          this.showError = false;
          this.startCountDown(
            res.data.requestRateLimitInSeconds || this.countdown
          );
        } else if (/\d/g.test(res)) {
          this.errorMessage = res;
          this.showError = true;
          this.startCountDown(res.match(/\d+/)[0]);
        }
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  verifyOtpCode() {
    this.authService.verifyOtpCode(this.data.email, this.otpCode).subscribe({
      next: (res: any) => {
        if (res && res.success && Object.keys(res.data).length) {
          this.ref.close(res.data); // Send CSRF token back on close
        } else if (/\d/g.test(res)) {
          this.errorMessage = res;
          this.showError = true;
          this.startCountDown(res.match(/\d+/)[0]);
        }
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  async initializeTurnstile(
    turnstileRef: ElementRef<HTMLDivElement>,
    resolved: (token: string) => void
  ): Promise<void> {
    if (!turnstileRef?.nativeElement || !this.turnstileSiteKey) {
      return;
    }

    if (!window.turnstile) {
      return;
    }

    this.turnstileWidgetId = window.turnstile.render(
      turnstileRef.nativeElement,
      {
        sitekey: this.turnstileSiteKey,
        size: 'normal',
        callback: (token: string) => resolved(token),
        'error-callback': () => {},
      }
    );
  }

  onOtpKeyDown = (event: KeyboardEvent): void => {
    const input = event.target as HTMLInputElement;
    if (
      input?.tagName !== 'INPUT' ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey
    ) {
      return;
    }

    if (/^[0-9]$/.test(event.key) && input.value) {
      this.setOtpDigit(event, input, event.key, 'insertText');
    } else if (event.key === 'Backspace') {
      const target = input.value ? input : this.getPreviousOtpInput(input);
      if (target) {
        target.focus();
        this.setOtpDigit(event, target, '', '');
      }
    }
  };

  private setOtpDigit(
    event: KeyboardEvent,
    input: HTMLInputElement,
    value: string,
    inputType: string
  ): void {
    event.preventDefault();
    event.stopPropagation();
    input.value = value;
    input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType }));
  }

  private getPreviousOtpInput(
    input: HTMLInputElement
  ): HTMLInputElement | null {
    let previous = input.previousElementSibling;
    while (previous && previous.tagName !== 'INPUT') {
      previous = previous.previousElementSibling;
    }
    return previous as HTMLInputElement | null;
  }

  ngOnDestroy(): void {
    this.otpInputRef?.nativeElement.removeEventListener(
      'keydown',
      this.onOtpKeyDown,
      true
    );
    if (this.turnstileWidgetId) {
      window.turnstile?.remove(this.turnstileWidgetId);
      this.turnstileWidgetId = null;
    }
  }
}
